import { Box, CheckBox, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/Inputs/DayPicker'
import EmployeeSelectorInput from 'modules/core/components/Inputs/EmployeeSelector'
import { useEffect } from 'modules/core/hooks/useEffect'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import type Employee from 'modules/employees/store/models/Employee'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { ShiftManagementFeatureFlags } from 'modules/shift_management/lib/consts/ShiftManagementFeatureFlags'
import type ShiftBulkDeleteForm from 'modules/shift_management/lib/forms/ShiftBulkDeleteForm'
import { getEmployeeCountCopy } from 'modules/shift_management/lib/getDescription'
import { maxDate, minDate } from 'modules/shift_management/lib/getMinMaxDates'

interface Props {
  form: ShiftBulkDeleteForm
  label: string
  selectableEmployees: Employee[]
  onSelectEmployee: (id: number | null | undefined) => void
  selectedEmployeeIds: number[]
  onCheckboxClick: () => void
  destroyBackUpShifts: boolean
  contractVersions: ContractVersions
  setDestroyAutomaticOnly: () => void
  destroyAutomaticOnly: boolean
}

const FormContent = ({
  form,
  label,
  selectableEmployees,
  onSelectEmployee,
  selectedEmployeeIds,
  onCheckboxClick,
  destroyBackUpShifts,
  contractVersions,
  setDestroyAutomaticOnly,
  destroyAutomaticOnly,
}: Props) => {
  const employeeCount = selectedEmployeeIds.length
  const startDate = form.get('start_at').value
  const endDate = form.get('end_at').value
  const minDateFromForm = startDate ? stringToDate(startDate, 'ui') : minDate
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const isAutomationEnabled = hasFeatureFlag(
    ShiftManagementFeatureFlags.DEV_SHIFT_MANAGEMENT_AUTOMATION
  )

  useEffect(() => {
    if (startDate) {
      form.setValues({ end_at: startDate })
    }
  }, [startDate]) // eslint-disable-line react-hooks/exhaustive-deps

  const showWarning = (): boolean => {
    return employeeCount > 0 && endDate && startDate
  }

  const warningDescription = getEmployeeCountCopy(employeeCount)

  const hasBackUpShifts = hasFeatureFlag(ShiftManagementFeatureFlags.SHIFT_MANAGEMENT_BACKUP_SHIFTS)

  return (
    <Box gap="s16">
      <Box flexDirection="row" gap="s16">
        <DayPicker
          field={form.get('start_at')}
          label={i18n.t('common.from')}
          minDate={minDate}
          maxDate={maxDate}
        />
        <DayPicker
          field={form.get('end_at')}
          label={i18n.t('common.to')}
          minDate={minDateFromForm}
          maxDate={maxDate}
        />
      </Box>
      <EmployeeSelectorInput
        hasSubmitButton
        isMultiSelect
        attachment="top center"
        employees={selectableEmployees}
        field={form.get('employee_id')}
        label={label}
        onSelectEmployee={onSelectEmployee}
        selectedEmployeeIds={selectedEmployeeIds}
        multiFilters
        contractVersions={contractVersions}
      />
      {hasBackUpShifts && (
        <Box flexDirection="row" gap="s8">
          <Box paddingY="s2">
            <CheckBox checked={destroyBackUpShifts} onClick={onCheckboxClick} />
          </Box>
          <Text.Normal size="200" weight="medium">
            {i18n.t('shift_management.backup_shifts.delete')}
          </Text.Normal>
        </Box>
      )}
      {isAutomationEnabled && (
        <Box flexDirection="row" gap="s8">
          <Box paddingY="s2">
            <CheckBox checked={destroyAutomaticOnly} onClick={setDestroyAutomaticOnly} />
          </Box>
          <Text.Normal size="200" weight="medium">
            {i18n.t('shift_management.coverage_section.automation.delete')}
          </Text.Normal>
        </Box>
      )}
      {showWarning() && (
        <Box
          paddingX="s24"
          paddingY="s16"
          background="champagne400"
          borderRadius={{
            all: 'abs008',
          }}
        >
          {i18n.tView('shift_management.bulk_delete.info', {
            start_date: <b>{startDate}</b>,
            end_date: <b>{endDate}</b>,

            count: (
              <b>
                {employeeCount}&nbsp;{warningDescription}
              </b>
            ),
          })}
        </Box>
      )}
    </Box>
  )
}

export default observer(FormContent)
